import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Save, Globe, CheckCircle2, XCircle, Lock } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useBusiness } from '../../contexts/BusinessContext';
import { authedFetch } from '../../lib/authedFetch';
import { useLanguage } from '../../contexts/LanguageContext';

// Canonical integration catalog (mirrors backend INTEGRATION_CATALOG). Tier gating is provider-specific
// and count-capped; secrets are masked and never echoed back ('***REDACTED***').
// B3: mirrors backend INTEGRATION_CATALOG. custom OUTRANKS enterprise (custom-only connectors don't
// leak to enterprise). execution_supported=true only where a real workflow exists today.
const TIER_RANK = { starter: 0, professional: 1, enterprise: 2, custom: 3 };
const TIER_CAP = { starter: 2, professional: 3, enterprise: -1, custom: -1 };
const REDACTED = '***REDACTED***';
const f = (key, label, type = 'text') => ({ key, label, type });
const CATALOG = [
  { id: 'whatsapp', name: 'WhatsApp Business', category: 'communication', min_tier: 'starter', execution_supported: true, fields: [f('phone_number', 'Phone number'), f('business_account_id', 'Business account ID'), f('api_token', 'API token', 'secret')] },
  { id: 'email', name: 'Email (SMTP)', category: 'communication', min_tier: 'starter', execution_supported: true, fields: [f('from_email', 'From email'), f('smtp_host', 'SMTP host'), f('smtp_user', 'SMTP user'), f('smtp_password', 'SMTP password', 'secret')] },
  { id: 'website', name: 'Website Widget', category: 'web', min_tier: 'professional', execution_supported: true, fields: [f('site_url', 'Site URL'), f('contact_form', 'Contact form', 'toggle'), f('live_chat', 'Live chat', 'toggle'), f('chatbot', 'Chatbot', 'toggle')] },
  { id: 'slack', name: 'Slack', category: 'communication', min_tier: 'professional', execution_supported: false, fields: [f('workspace', 'Workspace'), f('default_channel', 'Default channel'), f('api_token', 'Bot token', 'secret')] },
  { id: 'zapier', name: 'Zapier', category: 'automation', min_tier: 'professional', execution_supported: false, fields: [f('api_key', 'API key', 'secret')] },
  { id: 'webhooks', name: 'Webhooks / API', category: 'developer', min_tier: 'professional', execution_supported: false, fields: [f('endpoint_url', 'Endpoint URL'), f('signing_secret', 'Signing secret', 'secret')] },
  { id: 'connectwise', name: 'ConnectWise', category: 'psa', min_tier: 'enterprise', execution_supported: false, fields: [f('site_url', 'Site URL'), f('company_id', 'Company ID'), f('public_key', 'Public key'), f('private_key', 'Private key', 'secret')] },
  { id: 'monday', name: 'monday.com', category: 'project', min_tier: 'enterprise', execution_supported: false, fields: [f('board_id', 'Board ID'), f('api_token', 'API token', 'secret')] },
  { id: 'jira', name: 'Jira', category: 'itsm', min_tier: 'enterprise', execution_supported: false, fields: [f('base_url', 'Base URL'), f('project_key', 'Project key'), f('api_token', 'API token', 'secret')] },
  { id: 'solarwinds_sam', name: 'SolarWinds SAM', category: 'monitoring', min_tier: 'enterprise', execution_supported: false, fields: [f('hostname', 'Hostname'), f('username', 'Username'), f('password', 'Password', 'secret')] },
  { id: 'solarwinds_npm', name: 'SolarWinds NPM', category: 'monitoring', min_tier: 'enterprise', execution_supported: false, fields: [f('hostname', 'Hostname'), f('username', 'Username'), f('password', 'Password', 'secret')] },
  { id: 'facebook', name: 'Facebook Business Page', category: 'voice_of_customer', min_tier: 'custom', execution_supported: false, fields: [f('page_id', 'Page ID'), f('access_token', 'Page access token', 'secret')] },
  { id: 'instagram', name: 'Instagram Business', category: 'voice_of_customer', min_tier: 'custom', execution_supported: false, fields: [f('ig_business_id', 'Instagram business ID'), f('access_token', 'Access token', 'secret')] },
  { id: 'google_business', name: 'Google Business Profile', category: 'voice_of_customer', min_tier: 'custom', execution_supported: false, fields: [f('location_id', 'Location ID'), f('api_key', 'API key', 'secret')] },
  { id: 'twitter_x', name: 'Twitter/X Business Handle', category: 'voice_of_customer', min_tier: 'custom', execution_supported: false, fields: [f('handle', 'Handle'), f('bearer_token', 'Bearer token', 'secret')] },
  { id: 'voc_scanning', name: 'Voice of Customer Scanning', category: 'voice_of_customer', min_tier: 'custom', execution_supported: false, fields: [f('sources', 'Connected feedback sources')] },
];
const clientAllowed = (tier) => {
  const r = TIER_RANK[tier] ?? 0;
  return CATALOG.filter(c => (TIER_RANK[c.min_tier] ?? 0) <= r).map(c => c.id);
};

export const IntegrationsManager = () => {
  const { toast } = useToast();
  const { currentBusiness } = useBusiness();
  const { t } = useLanguage();
  const [tierId, setTierId] = useState('professional');
  const [tenantId, setTenantId] = useState(null);
  const [configs, setConfigs] = useState({});
  const [saving, setSaving] = useState({});
  const [statusMap, setStatusMap] = useState({});   // B3: canonical per-connector honest state from backend

  useEffect(() => {
    const brand = currentBusiness?.brandId;
    if (!brand) return;
    authedFetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/business/by-brand/${brand}`)
      .then(r => r.ok ? r.json() : null)
      .then(t => {
        if (!t) return;
        setTenantId(t.id);
        setTierId(t.tier_id || 'professional');
        const init = {};
        CATALOG.forEach(c => {
          const r = (t.integrations || {})[c.id] || {};
          const fv = { enabled: !!r.enabled };
          c.fields.forEach(fl => { fv[fl.key] = fl.type === 'toggle' ? (r[fl.key] === true) : (r[fl.key] ?? ''); });
          init[c.id] = fv;
        });
        setConfigs(init);
      })
      .catch(() => { /* offline / not registered yet */ });
  }, [currentBusiness?.brandId]);

  // B3: fetch canonical honest per-connector status (tier_locked / available_to_configure /
  // needs_credentials / configured + execution_supported). Falls back to local compute if unavailable.
  useEffect(() => {
    if (!tenantId) return;
    authedFetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/tenants/${tenantId}/integrations`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.integration_status?.length) {
          const m = {};
          d.integration_status.forEach(s => { m[s.id] = s; });
          setStatusMap(m);
        }
      })
      .catch(() => { /* fallback to local computation in getStatus */ });
  }, [tenantId]);

  // Honest state for a connector — prefer backend integration_status; else compute locally.
  const getStatus = (c) => {
    const st = statusMap[c.id];
    if (st) return { ok: !!st.allowed_by_tier, activation: st.activation_status, execSupported: !!st.execution_supported };
    const ok = clientAllowed(tierId).includes(c.id);
    const cfg = configs[c.id] || {};
    const valueKeys = c.fields.filter(fl => fl.type !== 'toggle').map(fl => fl.key);
    const secretKeys = c.fields.filter(fl => fl.type === 'secret').map(fl => fl.key);
    const hasAny = valueKeys.some(k => { const v = cfg[k]; return v != null && v !== '' && v !== REDACTED; });
    const missingSecret = secretKeys.some(k => !cfg[k]);
    const configured = hasAny && !missingSecret;
    const activation = !ok ? 'tier_locked' : configured ? 'configured' : hasAny ? 'needs_credentials' : 'available_to_configure';
    return { ok, activation, execSupported: c.execution_supported ?? false };
  };

  const allowed = clientAllowed(tierId);
  const cap = TIER_CAP[tierId] ?? 3;
  const enabledCount = Object.values(configs).filter(c => c?.enabled).length;
  const isAllowed = (id) => allowed.includes(id);
  const updateField = (id, key, val) => setConfigs(prev => ({ ...prev, [id]: { ...prev[id], [key]: val } }));

  const persist = async (c) => {
    if (!tenantId) {
      toast({ title: t('st.integ.notRegistered'), description: t('st.integ.notRegisteredDesc'), variant: 'destructive' });
      return;
    }
    setSaving(s => ({ ...s, [c.id]: true }));
    try {
      const cfg = { enabled: !!configs[c.id]?.enabled };
      c.fields.forEach(fl => {
        const v = configs[c.id]?.[fl.key];
        if (fl.type === 'toggle') cfg[fl.key] = v === true;
        else if (fl.type === 'secret') { if (v && v !== REDACTED) cfg[fl.key] = v; }   // omit redacted/empty secret
        else if (v) cfg[fl.key] = v;
      });
      const res = await authedFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/business/${tenantId}/integrations`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ integration: c.id, enabled: cfg.enabled, config: cfg }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || t('st.integ.saveFailed'));
      }
      toast({ title: t('st.integ.saved'), description: t('st.integ.savedDesc', { name: c.name }) });
    } catch (e) {
      toast({ title: t('common.error'), description: e.message, variant: 'destructive' });
    } finally {
      setSaving(s => ({ ...s, [c.id]: false }));
    }
  };

  // B3 honest badges — never "Connected" (config storage != live execution).
  const StatusBadge = ({ activation }) => {
    if (activation === 'tier_locked') return <Badge className="bg-gray-100 text-gray-600"><Lock className="h-3 w-3 mr-1" />{t('st.integ.upgrade')}</Badge>;
    if (activation === 'configured') return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="h-3 w-3 mr-1" />{t('st.integ.configured')}</Badge>;
    if (activation === 'needs_credentials') return <Badge className="bg-amber-100 text-amber-700"><XCircle className="h-3 w-3 mr-1" />{t('st.integ.needsCreds')}</Badge>;
    return <Badge variant="secondary">{t('st.integ.available')}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="integrations-manager">
      <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center"><Globe className="h-5 w-5 mr-2" />{t('st.integrations')}</span>
            <Badge className="bg-purple-100 text-purple-800 capitalize">{t('st.integ.planBadge', { tier: tierId })}</Badge>
          </CardTitle>
          <CardDescription>
            {t('st.integ.desc', { allowed: allowed.length, total: CATALOG.length })}
            {cap === -1 ? ` ${t('st.integ.unlimited')}` : ` ${t('st.integ.upTo', { cap, enabled: enabledCount })}`}
          </CardDescription>
        </CardHeader>
      </Card>

      {CATALOG.map(c => {
        const { ok, activation, execSupported } = getStatus(c);
        const cfg = configs[c.id] || {};
        return (
          <Card key={c.id} className="bg-white/70 backdrop-blur-sm border-0 shadow-lg" data-testid={`integration-${c.id}`}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center">{c.name}</span>
                <StatusBadge activation={activation} />
              </CardTitle>
              <CardDescription className="capitalize">{c.category}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {ok ? (
                <>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="font-medium">{t('st.integ.enable', { name: c.name })}</div>
                    <Switch checked={!!cfg.enabled} onCheckedChange={(v) => updateField(c.id, 'enabled', v)} data-testid={`${c.id}-toggle`} />
                  </div>
                  {!execSupported && (
                    <p className="text-xs text-gray-500" data-testid={`${c.id}-noexec-note`}>{t('st.integ.noExecNote')}</p>
                  )}
                  <div className="grid md:grid-cols-2 gap-4">
                    {c.fields.map(fl => fl.type === 'toggle' ? (
                      <div key={fl.key} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="font-medium">{fl.label}</div>
                        <Switch checked={cfg[fl.key] === true} onCheckedChange={(v) => updateField(c.id, fl.key, v)} data-testid={`${c.id}-${fl.key}-toggle`} />
                      </div>
                    ) : (
                      <div key={fl.key} className="space-y-2">
                        <Label>{fl.label}</Label>
                        <Input
                          type={fl.type === 'secret' ? 'password' : 'text'}
                          value={cfg[fl.key] || ''}
                          onChange={(e) => updateField(c.id, fl.key, e.target.value)}
                          placeholder={fl.label}
                          data-testid={`${c.id}-${fl.key}-input`}
                        />
                      </div>
                    ))}
                  </div>
                  <Button onClick={() => persist(c)} disabled={saving[c.id]} data-testid={`${c.id}-save-btn`} className="bg-gradient-to-r from-purple-500 to-indigo-600">
                    <Save className="h-4 w-4 mr-2" />{saving[c.id] ? t('st.savingEllipsis') : t('common.save')}
                  </Button>
                </>
              ) : (
                <p className="text-sm text-gray-500">{t('st.integ.higherPlan', { name: c.name })}</p>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default IntegrationsManager;
